import { useMemo } from 'preact/hooks';
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useRecoilValueLoadable,
} from 'recoil';

import { useAllParams } from './parameterStore';
import { multiParamToArray } from './parameterManagement';
import config from './config';

const initializationState = atom({
    key: 'INITIALIZATION_STATE',
    default:  config.launcherGenerator.getInitializationPromise()
});

const generationState = atom({
    key: 'GENERATION_STATE',
    default: {generating: false, error: false}
});

const GENERATION_TYPE_ZIP = "zip";
const GENERATION_TYPE_COMMANDS = "commands";
const GENERATION_TYPE_PREVIEW = "preview";
const GENERATION_TYPE_DIFF = "diff";

const useGenerate = () => {
    const parameters = useAllParams();
    const [generationStateValue, setGenerationState] = useRecoilState(generationState);

    return async (generateType) => {
        if (generationStateValue.generating) {
            return;
        }
        setGenerationState({generating: true, error: false});

        // Filter for a given project type
        const features = parameters.availableFeatures
            .map(f => f.name)
            .filter(name => parameters.features[name]);

        const parametersObj = {
            ...parameters,
            clouds: multiParamToArray(parameters.clouds),
            services: multiParamToArray(parameters.services),
            features: features,
            availableFeatures: undefined
        }

        try {
            switch(generateType) {
                case GENERATION_TYPE_ZIP:
                    await config.launcherGenerator.generateZip(parametersObj);
                    setGenerationState({generating: false, error: false});
                    break;
                case GENERATION_TYPE_COMMANDS:
                    const commandContents = await config.launcherGenerator.generateCommands(parametersObj);
                    setGenerationState({generating: false, error: false, contents: commandContents, isCommand: true });
                    break;
                case GENERATION_TYPE_PREVIEW:
                    const contents = await config.launcherGenerator.generatePreview(parametersObj);
                    setGenerationState({generating: false, error: false, contents: contents });
                    break;
                case GENERATION_TYPE_DIFF:
                    const diffContents = await config.launcherGenerator.generateDiff(parametersObj);
                    setGenerationState({generating: false, error: false, contents: diffContents, isDiff: true });
                    break;
                default:
                    const error = new Error("Unknown generation type '" + generateType + "'");
                    setGenerationState({generating: false, error});
            }
        } catch (error) {
            setGenerationState({generating: false, error: error})
        }
    }
};

const useGenerationLoading = () => {
    const generationStateValue = useRecoilValue(generationState);
    const initializationStateLoadable = useRecoilValueLoadable(initializationState);
    return generationStateValue.generating || initializationStateLoadable.state === "loading";
};


const useGenerationContents = () => {
    const generationStateValue = useRecoilValue(generationState);
    return generationStateValue.contents;
}

const useGenerationDiff = () => {
    const generationStateValue = useRecoilValue(generationState);
    return generationStateValue.isDiff;
}

const useGenerationCommand = () => {
    const generationStateValue = useRecoilValue(generationState);
    return generationStateValue.isCommand;
}

const useClearGeneration = () => {
    const [, setValue] = useRecoilState(generationState);
    return () => setValue( {generating: false, error: false});
}

const useGenerationError = () => {
    const generationStateValue = useRecoilValue(generationState);
    return useMemo(() => {
        if (generationStateValue?.error) {
            let message = generationStateValue?.error?.message || generationStateValue?.error?.toString();
            if (message === "GraalVM is not supported in Groovy applications") {
                message = "Groovy applications are incompatible with GraalVM Native Image because of their reliance on reflection";
            }
            return new Error(`Could not generate application. Reason: "${message}"`);
        }
        return null;
    }, [generationStateValue])
};

export {
    // Generation states
    useGenerate,
    useGenerationLoading,
    useGenerationError,
    useGenerationContents,
    useGenerationDiff,
    useGenerationCommand,
    useClearGeneration,
    GENERATION_TYPE_ZIP,
    GENERATION_TYPE_COMMANDS,
    GENERATION_TYPE_PREVIEW,
    GENERATION_TYPE_DIFF
};
